import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { Clock, ShieldCheck, AlertTriangle, Phone, Mail, Calendar, CreditCard, Home, BarChart3, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BuyerScoreCard, BuyerScoreBadge } from "@/components/BuyerScore";
import { LeadAnalytics } from "@/components/LeadAnalytics";
import { HuntModeToggle } from "@/components/HuntModeToggle";
import { InquiryReplyDialog } from "@/components/InquiryReplyDialog";

const StatusBadge = ({ s }) => {
  const map = {
    pending: { c: "chip-gold", icon: Clock, label: "Pending" },
    approved: { c: "chip-emerald", icon: ShieldCheck, label: "Live" },
    rejected: { c: "chip", icon: AlertTriangle, label: "Rejected" },
  };
  const m = map[s] || map.pending;
  const Icon = m.icon;
  return <span className={`chip ${m.c}`} data-testid={`listing-status-${s}`}><Icon className="w-3 h-3" /> {m.label}</span>;
};

const relTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? "yesterday" : `${d}d ago`;
};

export const MyListings = ({ onAuth }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [visits, setVisits] = useState([]);
  const [replyInquiry, setReplyInquiry] = useState(null);
  const [inquiryLocalityFilter, setInquiryLocalityFilter] = useState(null);

  useEffect(() => {
    if (!user) return;
    const run = async () => {
      try {
        const [l, i, p, v] = await Promise.all([
          api.get("/me/listings"),
          api.get("/me/inquiries"),
          api.get("/me/payments"),
          api.get("/visits"),
        ]);
        setListings(l.data);
        setInquiries(i.data);
        setPayments(p.data);
        setVisits(v.data);
        // Also fetch scored inquiries for owners/agents
        try {
          const scored = await api.get("/me/inquiries-scored");
          setInquiries(scored.data);
        } catch { /* ignore */ }
      } catch {
        // ignore — may 401 if not authed
      }
    };
    run();
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <h1 className="font-serif text-3xl text-[var(--teak)]">Sign in to see your dashboard</h1>
        <button className="btn-primary mt-6" onClick={() => onAuth?.("login")} data-testid="dashboard-signin-btn">Sign In</button>
      </div>
    );
  }

  const confirmVisit = async (id, status) => {
    try {
      await api.post(`/visits/${id}/status`, { status });
      toast.success(`Visit ${status}`);
      const { data } = await api.get("/visits");
      setVisits(data);
    } catch {
      toast.error("Failed");
    }
  };

  const tabs = [
    { key: "listings", label: "Listings", count: listings.length, icon: Home },
    { key: "inquiries", label: "Inquiries", count: inquiries.length, icon: Mail },
    { key: "visits", label: "Visits", count: visits.length, icon: Calendar },
    { key: "payments", label: "Payments", count: payments.length, icon: CreditCard },
    ...(listings.length > 0 ? [{ key: "analytics", label: "Analytics", count: null, icon: BarChart3 }] : []),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="section-eyebrow">Dashboard</span>
          <h1 className="font-serif text-4xl text-[var(--teak)] mt-2">Hello, {user.name.split(" ")[0]}</h1>
          <p className="text-[var(--ink-muted)] mt-2">Your listings, leads, visits and payment history.</p>
        </div>
        <HuntModeToggle />
      </div>

      <BuyerScoreCard />

      <div className="flex gap-2 flex-wrap mb-6 bg-white p-1 rounded-full border border-[var(--line)] w-fit" data-testid="dashboard-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`dashboard-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${tab === t.key ? "bg-[var(--teak)] text-white" : "text-[var(--ink-muted)]"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            <span className={`text-[10px] px-1.5 rounded-full ${tab === t.key ? "bg-white/20" : "bg-[var(--surface)]"}`}>{t.count ?? "AI"}</span>
          </button>
        ))}
      </div>

      {tab === "listings" && (
        <div className="space-y-4" data-testid="my-listings-list">
          {listings.length === 0 ? (
            <div className="text-center py-12 text-[var(--ink-muted)]">You haven&apos;t posted any properties yet. <Link to="/post" className="text-[var(--primary)] font-medium">Post one</Link></div>
          ) : listings.map((p) => (
            <div key={p.id} className="block p-4 bg-white rounded-2xl border border-[var(--line)] hover:shadow-md transition" data-testid={`my-listing-${p.id}`}>
              <div className="grid lg:grid-cols-12 gap-4 items-center">
                <Link to={`/property/${p.id}`} className="lg:col-span-2 aspect-[4/3] rounded-lg overflow-hidden bg-[var(--surface)] relative">
                  {p.images?.[0] && <img src={p.images[0]} alt="" className={`w-full h-full object-cover ${p.sold ? "grayscale opacity-70" : ""}`} />}
                  {p.sold && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="rotate-[-12deg] bg-red-600 text-white font-serif text-sm tracking-widest px-2 py-0.5 shadow-lg border-2 border-white/70 uppercase">Sold</div>
                    </div>
                  )}
                </Link>
                <Link to={`/property/${p.id}`} className="lg:col-span-5 block">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <StatusBadge s={p.status} />
                    {p.sold && <span className="chip text-[10px] bg-red-100 text-red-800 border-red-300">SOLD</span>}
                    {p.ai_verdict && <span className="chip text-[10px]">AI: {p.ai_verdict}</span>}
                  </div>
                  <h3 className="font-serif text-lg text-[var(--teak)]">{p.title}</h3>
                  <div className="text-xs text-[var(--ink-muted)] mt-1">{p.locality} · {p.bhk} BHK · {p.area_sqft} sq.ft</div>
                  {p.verification_note && <div className="text-xs text-[var(--ink-muted)] italic mt-1">&ldquo;{p.verification_note}&rdquo;</div>}
                </Link>
                <div className="lg:col-span-2 text-right">
                  <div className="font-mono text-lg font-bold text-[var(--primary)]">{formatINR(p.price, p.listing_type)}</div>
                </div>
                <div className="lg:col-span-3 flex flex-col gap-1">
                  <div className="flex gap-3 text-xs text-[var(--ink-muted)]">
                    <span>📩 {p.inquiries_count || 0} leads</span>
                    <span>📅 {p.visits_count || 0} visits</span>
                  </div>
                  {p.status === "approved" && (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/properties/${p.id}/${p.sold ? "unmark-sold" : "mark-sold"}`);
                          toast.success(p.sold ? "Listing is live again" : "Marked as Sold");
                          const { data } = await api.get("/me/listings");
                          setListings(data);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed");
                        }
                      }}
                      className={`chip text-[11px] ${p.sold ? "chip-emerald" : "bg-red-50 text-red-700 border-red-200"} hover:brightness-95 cursor-pointer`}
                      data-testid={`my-listing-sold-${p.id}`}
                    >
                      {p.sold ? "Unmark Sold" : "Mark as Sold"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-3" data-testid="my-inquiries-list">
          {inquiryLocalityFilter && (
            <div
              className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[rgba(200,90,50,0.08)] border border-[rgba(200,90,50,0.25)]"
              data-testid="inquiry-locality-filter"
            >
              <div className="text-sm text-[var(--teak)]">
                Filtered by locality · <b>{inquiryLocalityFilter}</b>
              </div>
              <button
                onClick={() => setInquiryLocalityFilter(null)}
                className="text-xs text-[var(--primary)] hover:underline"
                data-testid="clear-locality-filter"
              >
                Clear filter
              </button>
            </div>
          )}
          {(() => {
            const filtered = inquiryLocalityFilter
              ? inquiries.filter((i) => i.property_locality === inquiryLocalityFilter)
              : inquiries;
            if (filtered.length === 0) {
              return (
                <div className="text-center py-12 text-[var(--ink-muted)]">
                  {inquiryLocalityFilter ? `No inquiries in ${inquiryLocalityFilter} yet.` : "No inquiries yet."}
                </div>
              );
            }
            return filtered.map((i) => (
            <div key={i.id} className="p-4 bg-white rounded-2xl border border-[var(--line)]" data-testid={`inquiry-${i.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`chip ${i.kind === "spam" ? "chip" : "chip-emerald"}`}>{i.kind || "lead"}</span>
                  {i.buyer_score && <BuyerScoreBadge level={i.buyer_score.level} score={i.buyer_score.score} />}
                  <span className="text-xs text-[var(--ink-muted)]">{new Date(i.created_at).toLocaleString()}</span>
                </div>
                <Link to={`/property/${i.property_id}`} className="text-xs text-[var(--primary)]">{i.property_title}</Link>
              </div>
              <div className="font-serif text-base text-[var(--teak)]">{i.name}</div>
              <p className="text-sm text-[var(--ink-muted)] mt-1">{i.message}</p>
              {i.replied && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5"
                  data-testid={`inquiry-reply-chip-${i.id}`}
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Replied {relTime(i.last_reply_at)}
                  {i.last_invoice_no && <span>· invoice {i.last_invoice_no}</span>}
                </div>
              )}
              <div className="flex gap-3 mt-3 text-xs items-center flex-wrap">
                <a href={`tel:${i.phone}`} className="flex items-center gap-1 text-[var(--primary)]"><Phone className="w-3 h-3" /> {i.phone}</a>
                <a href={`mailto:${i.email}`} className="flex items-center gap-1 text-[var(--primary)]"><Mail className="w-3 h-3" /> {i.email}</a>
                {i.kind !== "spam" && (
                  <button
                    onClick={() => setReplyInquiry(i)}
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-[var(--primary)]/40 bg-[var(--primary)]/10 text-[var(--primary)] px-3 py-1 text-xs font-medium hover:bg-[var(--primary)]/15"
                    data-testid={`reply-with-invoice-${i.id}`}
                  >
                    <MessageCircle className="w-3 h-3" /> Reply with Invoice
                  </button>
                )}
              </div>
            </div>
            ));
          })()}
        </div>
      )}

      {tab === "visits" && (
        <div className="space-y-3" data-testid="my-visits-list">
          {visits.length === 0 ? (
            <div className="text-center py-12 text-[var(--ink-muted)]">No visits scheduled.</div>
          ) : visits.map((v) => {
            const isOwner = v.owner_id === user.id;
            return (
              <div key={v.id} className="p-4 bg-white rounded-2xl border border-[var(--line)] flex items-center justify-between gap-3 flex-wrap" data-testid={`visit-${v.id}`}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-[var(--primary)]" />
                    <span className="font-serif text-base text-[var(--teak)]">{v.date} · {v.time_label}</span>
                    <span className="chip text-[10px]">{v.status}</span>
                  </div>
                  <div className="text-xs text-[var(--ink-muted)]">
                    <Link to={`/property/${v.property_id}`} className="text-[var(--primary)]">{v.property_title}</Link>
                    {" · "}
                    {isOwner ? `Buyer: ${v.buyer_name} (${v.buyer_phone || v.buyer_email})` : `Owner: ${v.owner_name}`}
                  </div>
                  {v.notes && <div className="text-xs italic text-[var(--ink-muted)] mt-1">&ldquo;{v.notes}&rdquo;</div>}
                </div>
                <div className="flex gap-2">
                  {isOwner && v.status === "requested" && (
                    <button onClick={() => confirmVisit(v.id, "confirmed")} className="btn-primary py-1.5 px-3 text-xs" data-testid={`confirm-visit-${v.id}`}>Confirm</button>
                  )}
                  {v.status !== "cancelled" && v.status !== "completed" && (
                    <button onClick={() => confirmVisit(v.id, "cancelled")} className="btn-ghost py-1.5 px-3 text-xs" data-testid={`cancel-visit-${v.id}`}>Cancel</button>
                  )}
                  {isOwner && v.status === "confirmed" && (
                    <button onClick={() => confirmVisit(v.id, "completed")} className="btn-ghost py-1.5 px-3 text-xs" data-testid={`complete-visit-${v.id}`}>Mark Done</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "payments" && (
        <div className="space-y-3" data-testid="my-payments-list">
          {payments.length === 0 ? (
            <div className="text-center py-12 text-[var(--ink-muted)]">No payments yet.</div>
          ) : payments.map((p) => (
            <div key={p.id} className="p-4 bg-white rounded-2xl border border-[var(--line)] flex items-center justify-between gap-3" data-testid={`payment-${p.id}`}>
              <div>
                <div className="font-serif text-base text-[var(--teak)] capitalize">
                  {p.purpose.replace("_", " ")} {p.mock && <span className="chip text-[10px] ml-1">mock</span>}
                </div>
                <div className="text-xs text-[var(--ink-muted)]">{new Date(p.created_at).toLocaleString()} · Order {p.order_id?.slice(-10)}</div>
              </div>
              <div className="text-right flex items-center gap-3">
                <div>
                  <div className="font-mono text-lg font-bold text-[var(--primary)]">₹{(p.amount_paise / 100).toLocaleString("en-IN")}</div>
                  <span className={`chip ${p.status === "paid" ? "chip-emerald" : "chip"}`}>{p.status}</span>
                </div>
                {p.status === "paid" && p.receipt_url && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/${p.order_id}/receipt`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost text-xs py-1.5 px-3"
                    data-testid={`receipt-${p.id}`}
                  >
                    <CreditCard className="w-3.5 h-3.5" /> Receipt PDF
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "analytics" && (
        <LeadAnalytics
          onLocalityClick={(label) => {
            if (!label) return;
            setInquiryLocalityFilter(label);
            setTab("inquiries");
            toast.success(`Filtered inquiries by ${label}`);
          }}
        />
      )}

      <InquiryReplyDialog
        open={!!replyInquiry}
        onOpenChange={(o) => !o && setReplyInquiry(null)}
        inquiry={replyInquiry}
        onReplied={async (res) => {
          // Optimistically mark replied + refresh from server
          setInquiries((list) =>
            list.map((x) =>
              x.id === replyInquiry?.id
                ? {
                    ...x,
                    replied: true,
                    last_reply_at: new Date().toISOString(),
                    last_invoice_no: res?.attached_invoice_no || x.last_invoice_no,
                  }
                : x,
            ),
          );
          try {
            const { data } = await api.get("/me/inquiries-scored");
            setInquiries(data);
          } catch { /* ignore */ }
        }}
      />
    </div>
  );
};
